import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { isCentralOperationsRole, isCentralRole, isOperatorRole } from './authSlice';

const DEFAULT_ORGANIZATION_ID = process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';
const MEDICAL_BILLING_FINAL_STATUSES = new Set(['exported', 'submitted', 'paid', 'rejected']);

const shouldAutoMarkMedicalBillingReady = (rideData, patchData = {}) => {
  const hasMedicalOrder = patchData.medicalTransportOrderId || rideData.medicalTransportOrderId;
  if (!hasMedicalOrder) return false;

  const nextStatus = patchData.status || rideData.status;
  const nextProofStatus = patchData.medicalTransportProofStatus || rideData.medicalTransportProofStatus;
  const nextBillingStatus = patchData.medicalTransportBillingStatus || rideData.medicalTransportBillingStatus;

  return nextStatus === 'completed' &&
    nextProofStatus === 'complete' &&
    !MEDICAL_BILLING_FINAL_STATUSES.has(nextBillingStatus) &&
    patchData.medicalTransportBillingStatus === undefined;
};

const getCurrentUser = (state) => state.auth?.user || null;

const hasOwnField = (data, field) => Object.prototype.hasOwnProperty.call(data || {}, field);

const addMissingField = (target, source, field, value) => {
  if (!hasOwnField(source, field) && value !== undefined) {
    target[field] = value;
  }
};

const buildMissingRecurringRideFields = (rideData, currentUser, inferredIsPhoneCustomer = true) => {
  const fields = {};
  const isMedicalRide = rideData?.isKrankenfahrt === true || !!rideData?.medicalTransportOrderId;

  addMissingField(fields, rideData, 'organizationId', currentUser?.organizationId || DEFAULT_ORGANIZATION_ID);
  addMissingField(fields, rideData, 'is_active', true);
  addMissingField(fields, rideData, 'driverVehicleId', null);
  addMissingField(fields, rideData, 'fare', null);
  addMissingField(fields, rideData, 'estimatedTimeMinutes', null);
  addMissingField(fields, rideData, 'distanceKm', null);
  addMissingField(fields, rideData, 'notes', null);
  addMissingField(fields, rideData, 'specialCapabilities', rideData?.specialVehicleType ? [rideData.specialVehicleType] : []);
  addMissingField(fields, rideData, 'isKrankenfahrt', false);
  addMissingField(fields, rideData, 'medicalTransportOrderId', null);
  addMissingField(fields, rideData, 'medicalTransportSnapshot', null);
  addMissingField(fields, rideData, 'medicalTransportBillingStatus', isMedicalRide ? 'not_ready' : null);
  addMissingField(fields, rideData, 'medicalTransportProofStatus', isMedicalRide ? 'missing' : null);
  addMissingField(fields, rideData, 'isRechnungsfahrt', false);
  addMissingField(fields, rideData, 'isPhoneCustomer', inferredIsPhoneCustomer);

  return fields;
};

const getOperatorScope = (state) => {
  const currentUser = getCurrentUser(state);
  return isOperatorRole(currentUser) ? currentUser.operatorId || null : null;
};

const assertOperatorRideAccess = (currentUser, rideData) => {
  if (isCentralOperationsRole(currentUser)) return;
  if (!isOperatorRole(currentUser) || !currentUser.operatorId) {
    throw new Error('Keine Berechtigung');
  }
  const visibleToOperatorIds = rideData.visibleToOperatorIds || [];
  if (
    rideData.createdByOperatorId !== currentUser.operatorId &&
    rideData.driverOperatorId !== currentUser.operatorId &&
    !visibleToOperatorIds.includes(currentUser.operatorId)
  ) {
    throw new Error('Fahrt gehoert zu einem anderen Unternehmer');
  }
};

// Async thunks
export const fetchRides = createAsyncThunk(
  'rides/fetchRides',
  async ({ status, limit: queryLimit = 20 } = {}, { getState, rejectWithValue }) => {
    try {
      console.log('Starte fetchRides...');
      const operatorScope = getOperatorScope(getState());
      let ridesQuery;
      
      if (status) {
        // Status kann ein String oder ein Array von Strings sein
        if (Array.isArray(status)) {
          console.log(`Erstelle Rides-Query mit Status "${status.join(',')}"...`);
          ridesQuery = operatorScope
            ? query(
                collection(db, 'rides'),
                where('visibleToOperatorIds', 'array-contains', operatorScope),
                where('status', 'in', status),
                orderBy('timestamp', 'desc'),
                firestoreLimit(queryLimit)
              )
            : query(
                collection(db, 'rides'),
                where('status', 'in', status),
                orderBy('timestamp', 'desc'),
                firestoreLimit(queryLimit)
              );
        } else {
          console.log(`Erstelle Rides-Query mit Status "${status}"...`);
          ridesQuery = operatorScope
            ? query(
                collection(db, 'rides'),
                where('visibleToOperatorIds', 'array-contains', operatorScope),
                where('status', '==', status),
                orderBy('timestamp', 'desc'),
                firestoreLimit(queryLimit)
              )
            : query(
                collection(db, 'rides'),
                where('status', '==', status),
                orderBy('timestamp', 'desc'),
                firestoreLimit(queryLimit)
              );
        }
      } else {
        // Wenn kein Status angegeben wird, lade nur aktive Fahrten
        const activeStatusList = ['open', 'accepted', 'in_progress', 'arrived', 'scheduled', 'pre_accepted', 'dispatcher_pending'];
        console.log(`Erstelle Rides-Query mit Status "${activeStatusList.join(',')}"...`);
        ridesQuery = operatorScope
          ? query(
              collection(db, 'rides'),
              where('visibleToOperatorIds', 'array-contains', operatorScope),
              where('status', 'in', activeStatusList),
              orderBy('timestamp', 'desc'),
              firestoreLimit(queryLimit)
            )
          : query(
              collection(db, 'rides'),
              where('status', 'in', activeStatusList),
              orderBy('timestamp', 'desc'),
              firestoreLimit(queryLimit)
            );
      }
      
      console.log('Führe Rides-Query aus...');
      const querySnapshot = await getDocs(ridesQuery);
      console.log('Query-Ergebnis:', querySnapshot.size, 'Fahrten gefunden');
      const rides = [];
      
      for (const rideDoc of querySnapshot.docs) {
        const rideData = rideDoc.data();
        
        // Fahrerdaten laden, falls vorhanden
        let driverData = null;
        if (rideData.driverId) {
          const driverDoc = await getDoc(doc(db, 'users', rideData.driverId));
          if (driverDoc.exists() && driverDoc.data().isDriver) {
            driverData = {
              id: rideData.driverId,
              ...driverDoc.data(),
              name: driverDoc.data().name,
              profilePic: driverDoc.data().profilePic || '',
              rating: driverDoc.data().rating || 0,
              ratingCount: driverDoc.data().ratingCount || 0
            };
          }
        }
        
        // Kundendaten laden
        let customerData = null;
        if (rideData.customerId) {
          const customerDoc = await getDoc(doc(db, 'users', rideData.customerId));
          if (customerDoc.exists()) {
            customerData = {
              id: rideData.customerId,
              name: customerDoc.data().name,
              email: customerDoc.data().email,
              phone: customerDoc.data().phone || '',
              profilePic: customerDoc.data().profilePic || ''
            };
          }
        }
        
        // Fahrzeugdaten laden, wenn verfügbar in users oder Vehicles
        let vehicleData = null;
        if (rideData.driverModel && rideData.driverLicensePlate) {
          // Verwende die in der Fahrt gespeicherten Fahrzeugdaten
          vehicleData = {
            model: rideData.driverModel,
            licensePlate: rideData.driverLicensePlate,
            vehicleType: rideData.vehicleType || 1
          };
        } else if (driverData && driverData.car && driverData.car.vehicleId) {
          // Fahrzeugdaten aus der Fahrzeug-Sammlung laden
          const vehicleDoc = await getDoc(doc(db, 'Vehicles', driverData.car.vehicleId));
          if (vehicleDoc.exists()) {
            vehicleData = {
              id: vehicleDoc.id,
              model: vehicleDoc.data().model,
              licensePlate: vehicleDoc.data().licensePlate,
              vehicleType: vehicleDoc.data().vehicleType || 1
            };
          }
        }
        
        // Zeitstempel konvertieren, mit Fallback-Optionen
        const processedRide = {
          id: rideDoc.id,
          ...rideData,
          customer: customerData,
          driver: driverData,
          vehicle: vehicleData,
        };
        
        // Konvertiere alle Zeitstempel für einfachere Handhabung
        const timestampFields = [
          'timestamp', 'acceptedTimestamp', 'acceptedAt', 'arrivedTimestamp', 
          'passengerPickedUpTimestamp', 'completionTimestamp', 'scheduledTime', 'startedAt'
        ];
        
        timestampFields.forEach(field => {
          if (rideData[field]) {
            processedRide[field] = rideData[field].toDate().toISOString();
          }
        });
        
        rides.push(processedRide);
      }
      
      return rides;
    } catch (error) {
      console.error('Error fetching rides:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRideById = createAsyncThunk(
  'rides/fetchRideById',
  async (rideId, { getState, rejectWithValue }) => {
    try {
      const rideDoc = await getDoc(doc(db, 'rides', rideId));
      
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      const rideData = rideDoc.data();
      assertOperatorRideAccess(getCurrentUser(getState()), rideData);
      
      // Fahrerdaten laden, falls vorhanden
      let driverData = null;
      if (rideData.driverId) {
        const driverDoc = await getDoc(doc(db, 'users', rideData.driverId));
        if (driverDoc.exists() && driverDoc.data().isDriver) {
          driverData = {
            id: rideData.driverId,
            ...driverDoc.data(),
            name: driverDoc.data().name,
            profilePic: driverDoc.data().profilePic || '',
            rating: driverDoc.data().rating || 0,
            ratingCount: driverDoc.data().ratingCount || 0
          };
        }
      }
      
      // Kundendaten laden
      let customerData = null;
      if (rideData.customerId) {
        const customerDoc = await getDoc(doc(db, 'users', rideData.customerId));
        if (customerDoc.exists()) {
          customerData = {
            id: rideData.customerId,
            name: customerDoc.data().name,
            email: customerDoc.data().email,
            phone: customerDoc.data().phone || '',
            profilePic: customerDoc.data().profilePic || ''
          };
        }
      }
      
      // Fahrzeugdaten laden, wenn verfügbar in users oder Vehicles
      let vehicleData = null;
      if (rideData.driverModel && rideData.driverLicensePlate) {
        // Verwende die in der Fahrt gespeicherten Fahrzeugdaten
        vehicleData = {
          model: rideData.driverModel,
          licensePlate: rideData.driverLicensePlate,
          vehicleType: rideData.vehicleType || 1
        };
      } else if (driverData && driverData.car && driverData.car.vehicleId) {
        // Fahrzeugdaten aus der Fahrzeug-Sammlung laden
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', driverData.car.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = {
            id: vehicleDoc.id,
            model: vehicleDoc.data().model,
            licensePlate: vehicleDoc.data().licensePlate,
            vehicleType: vehicleDoc.data().vehicleType || 1
          };
        }
      }
      
      // Zeitstempel konvertieren, mit Fallback-Optionen
      const processedRide = {
        id: rideDoc.id,
        ...rideData,
        customer: customerData,
        driver: driverData,
        vehicle: vehicleData,
      };
      
      // Konvertiere alle Zeitstempel für einfachere Handhabung
      const timestampFields = [
        'timestamp', 'acceptedTimestamp', 'acceptedAt', 'arrivedTimestamp', 
        'passengerPickedUpTimestamp', 'completionTimestamp', 'scheduledTime', 'startedAt'
      ];
      
      timestampFields.forEach(field => {
        if (rideData[field]) {
          processedRide[field] = rideData[field].toDate().toISOString();
        }
      });
      
      return processedRide;
    } catch (error) {
      console.error('Error fetching ride by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createRide = createAsyncThunk(
  'rides/createRide',
  async (rideData, { getState, rejectWithValue }) => {
    try {
      const currentUser = getCurrentUser(getState());
      const operatorId = isOperatorRole(currentUser) ? currentUser.operatorId : null;
      const { 
        customerId, 
        customerName,
        start,
        startLatitude,
        startLongitude,
        destination,
        destinationLatitude,
        destinationLongitude,
        vehicleType = 1,
        status = 'open', // Default-Status
        orderType = 1, // Sofortbestellung
        scheduledTime = null, // Für Vorbestellungen
        isRecurring = false, // Für Daueraufträge
        recurringOrderId = null // ID des Dauerauftrags
      } = rideData;
      
      // Grundlegende Felder für alle Bestelltypen
      const baseRide = {
        organizationId: currentUser?.organizationId || DEFAULT_ORGANIZATION_ID,
        createdByUserId: currentUser?.uid || null,
        createdByUserName: currentUser?.displayName || currentUser?.name || currentUser?.email || null,
        createdByUserEmail: currentUser?.email || null,
        createdByRole: currentUser?.role || 'system',
        createdByOperatorId: operatorId,
        preferredOperatorId: operatorId,
        dispatchScope: operatorId ? 'operator_preferred' : 'central_pool',
        visibleToOperatorIds: operatorId ? [operatorId] : [],
        customerId,
        customerName,
        customerPhone: rideData.customerPhone || null, // Telefonnummer des Kunden hinzufügen
        start,
        startLatitude,
        startLongitude,
        destination,
        destinationLatitude,
        destinationLongitude,
        vehicleType,
        status,
        orderType,
        timestamp: Timestamp.now(),
        ...(rideData.is_active !== undefined ? { is_active: rideData.is_active } : {}),
        // Weitere Felder - optional oder basierend auf dem Status
        driverId: rideData.driverId || null,
        driverName: rideData.driverName || null,
        driverProfilePic: rideData.driverProfilePic || null,
        driverModel: rideData.driverModel || null,
        driverLicensePlate: rideData.driverLicensePlate || null,
        driverRating: rideData.driverRating || null,
        driverRatingCount: rideData.driverRatingCount || null,
        driverVehicleId: rideData.driverVehicleId || null,
        // Disponenten-Zuweisung Flag (für Cloud Function Push-Notification)
        ...(rideData.assignedByDispatcher ? { assignedByDispatcher: true } : {}),
        // Akzeptiert-Zeitstempel (wenn Fahrer direkt zugewiesen)
        ...(rideData.acceptedTimestamp ? { acceptedTimestamp: Timestamp.now() } : {}),
        ...(rideData.acceptedAt ? { acceptedAt: Timestamp.now() } : {}),
        // Füge Fahrpreis hinzu, falls vorhanden
        fare: rideData.fare || null,
        // Füge geschätzte Fahrtdauer hinzu, falls vorhanden
        estimatedTimeMinutes: rideData.estimatedTimeMinutes || null,
        // Streckenlänge (wird für Fahrpreis-Berechnung in der Fahrer-App gebraucht)
        distanceKm: rideData.distanceKm || null,
        // Notizen für den Fahrer
        notes: rideData.notes || null,
        // Fahrttyp-Flags
        isKrankenfahrt: rideData.isKrankenfahrt || false,
        medicalTransportOrderId: rideData.medicalTransportOrderId || null,
        medicalTransportSnapshot: rideData.medicalTransportSnapshot || null,
        medicalTransportBillingStatus: rideData.medicalTransportBillingStatus || null,
        medicalTransportProofStatus: rideData.medicalTransportProofStatus || null,
        // Nachvollziehbarkeit einer bewusst über Kontingent/Zeitraum hinaus
        // angelegten Krankenfahrt (Disponent hat den Hinweis bestätigt).
        medicalTransportWarningOverride: rideData.medicalTransportWarningOverride || null,
        isRechnungsfahrt: rideData.isRechnungsfahrt || false,
        invoiceCustomerId: rideData.invoiceCustomerId || null,
        invoiceCustomerName: rideData.invoiceCustomerName || null,
        invoiceBillingStatus: rideData.invoiceBillingStatus || null,
        isPhoneCustomer: rideData.isPhoneCustomer || false,
        ...(rideData.isFestpreis ? {
          isFestpreis: true,
          festpreisEur: rideData.festpreisEur,
          festpreisLabel: rideData.festpreisLabel,
          estimatedEarningEur: rideData.estimatedEarningEur,
        } : {}),
        // Spezialfähigkeiten (Array)
        specialCapabilities: rideData.specialCapabilities || [],
        // Herkunft aus eingehendem Anruf (KI-Telefonist / Anruf-Benachrichtigung),
        // damit die Fahrt der Anruf-Session zugeordnet werden kann.
        ...(rideData.incomingCallSessionId ? {
          incomingCallSessionId: rideData.incomingCallSessionId,
          bookingSource: rideData.bookingSource || 'incoming_call',
        } : {}),
        // Dauerauftrag-Felder
        isRecurring,
        recurringOrderId,
      };
      
      // Spezifische Felder je nach Bestelltyp
      let specificFields = {};
      
      if (orderType === 1) { // Vorbestellung
        if (!scheduledTime) {
          throw new Error('Vorbestellungen benötigen einen geplanten Zeitpunkt');
        }
        
        // Bereite das Datum für die Datenbank vor (als UTC Timestamp)
        const scheduledDate = new Date(scheduledTime);
        
        // Wir stellen sicher, dass das Datum im korrekten Format für Firebase gespeichert wird
        specificFields = {
          scheduledTime: Timestamp.fromDate(scheduledDate),
          is_active: true,
        };
      } else { // Sofortbestellung (orderType === 2)
        // Keine spezifischen Felder für Sofortbestellungen notwendig
      }
      
      // Kombinieren der Daten
      const newRide = {
        ...baseRide,
        ...specificFields
      };
      
      const docRef = await addDoc(collection(db, 'rides'), newRide);
      
      return {
        id: docRef.id,
        ...newRide,
        timestamp: newRide.timestamp.toDate().toISOString(),
        scheduledTime: newRide.scheduledTime ? newRide.scheduledTime.toDate().toISOString() : null
      };
    } catch (error) {
      console.error('Error creating ride:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const assignRide = createAsyncThunk(
  'rides/assignRide',
  async ({ rideId, driverId }, { getState, rejectWithValue, dispatch }) => {
    try {
      // Fahrt laden
      const rideRef = doc(db, 'rides', rideId);
      const rideDoc = await getDoc(rideRef);
      
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      const rideData = rideDoc.data();
      const currentUser = getCurrentUser(getState());
      assertOperatorRideAccess(currentUser, rideData);
      
      // Prüfen, ob Fahrt in einem Status ist, in dem eine (Neu-)Zuweisung erlaubt ist
      const reassignableStatuses = ['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'];
      if (rideData.driverId && !reassignableStatuses.includes(rideData.status)) {
        throw new Error('Fahrt ist bereits einem Fahrer zugewiesen und kann in diesem Status nicht neu zugewiesen werden');
      }

      // Alten Fahrer aufräumen, falls Fahrt bereits zugewiesen war
      const previousDriverId = rideData.driverId;
      if (previousDriverId && previousDriverId !== driverId) {
        // driverLocations des alten Fahrers bereinigen (leerer String, wie Mobile-App erwartet)
        const oldLocationRef = doc(db, 'driverLocations', previousDriverId);
        const oldLocationDoc = await getDoc(oldLocationRef);
        if (oldLocationDoc.exists() && oldLocationDoc.data().orderId === rideId) {
          await updateDoc(oldLocationRef, {
            orderId: '',
            timestamp: Timestamp.now()
          });
        }

        // Fahrt als reassigned markieren, damit Mobile-App des alten Fahrers reagiert
        await updateDoc(rideRef, {
          previousDriverId: previousDriverId,
          reassignedAt: Timestamp.now()
        });

        // Benachrichtigung an alten Fahrer in notifications-Collection schreiben
        const oldDriverDoc = await getDoc(doc(db, 'users', previousDriverId));
        const oldDriverFcmToken = oldDriverDoc.exists() ? oldDriverDoc.data().fcmToken : null;
        await addDoc(collection(db, 'notifications'), {
          userId: previousDriverId,
          title: 'Fahrt neu zugewiesen',
          body: `Die Fahrt wurde vom Disponenten einem anderen Fahrer zugewiesen.`,
          data: {
            type: 'ride_reassigned',
            rideId: rideId
          },
          read: false,
          createdAt: Timestamp.now(),
          ...(oldDriverFcmToken ? { fcmToken: oldDriverFcmToken } : {})
        });
      }

      // Fahrerdaten laden
      const driverRef = doc(db, 'users', driverId);
      const driverDoc = await getDoc(driverRef);
      
      if (!driverDoc.exists() || !driverDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      const driverData = driverDoc.data();
      if (isOperatorRole(currentUser) && driverData.operatorId !== currentUser.operatorId) {
        throw new Error('Unternehmer duerfen nur eigene Fahrer zuweisen');
      }

      const isDeferredRideAssignment =
        (rideData.orderType === 1 || rideData.orderType === 3) &&
        rideData.convertedToOpenRide !== true;

      // Für sofort fahrbare Fahrten: Fahrer muss ein Fahrzeug zugewiesen haben
      if (!isDeferredRideAssignment && !driverData.car) {
        throw new Error('Diesem Fahrer ist kein Fahrzeug zugewiesen. Für sofort fahrbare Fahrten muss ein Fahrzeug vorhanden sein.');
      }

      // Fahrzeugdaten laden, falls vorhanden
      let vehicleData = null;
      if (driverData.car && driverData.car.vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', driverData.car.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = vehicleDoc.data();
          if (isOperatorRole(currentUser) && vehicleData.operatorId !== currentUser.operatorId) {
            throw new Error('Unternehmer duerfen nur eigene Fahrzeuge zuweisen');
          }
        }
      }
      
      // Status basierend auf OrderType bestimmen
      let newStatus;
      if (isDeferredRideAssignment) {
        // Vor-/Dauerbestellungen werden direkt zugewiesen (kein Annahme-Overlay)
        newStatus = 'pre_accepted';
      } else {
        // Sofort fahrbare Fahrten: Fahrer muss über Overlay annehmen
        newStatus = 'dispatcher_pending';
      }
      
      // Grundlegende Aktualisierungsdaten für alle Bestelltypen
      const baseUpdateData = {
        driverId,
        driverName: driverData.name,
        driverProfilePic: driverData.profilePic || '',
        driverRating: driverData.rating || null,
        driverRatingCount: driverData.ratingCount || null,
        driverOperatorId: driverData.operatorId || null,
        status: newStatus,
        assignedByDispatcher: true, // Markierung für Cloud Function: Push an Fahrer senden
        ...(newStatus === 'dispatcher_pending' ? { is_active: false } : {})
      };

      const hadCloudDispatch =
        rideData.currentOfferDriverId ||
        rideData.dispatchMethod === 'sectors' ||
        rideData.dispatchMethod === 'distance';

      if (hadCloudDispatch) {
        baseUpdateData.dispatchState = 'dispatcher_assigned';
        baseUpdateData.dispatchResetReason = 'dispatcher_direct_assignment';
        baseUpdateData.dispatchUpdatedAt = Timestamp.now();
      }

      if (previousDriverId && previousDriverId !== driverId) {
        baseUpdateData.dispatchRejectedDriverIds = arrayUnion(previousDriverId);
      }

      if (rideData.currentOfferDriverId) {
        baseUpdateData.currentOfferDriverId = null;
        baseUpdateData.currentOfferType = null;
        baseUpdateData.currentOfferStartedAt = null;
        baseUpdateData.currentOfferExpiresAt = null;
        baseUpdateData.dispatchAction = null;
        baseUpdateData.dispatchOfferHistory = arrayUnion({
          driverId: rideData.currentOfferDriverId,
          sectorId: rideData.pickupSectorId || null,
          source: rideData.dispatchOfferSource || 'dispatcher_interrupted_offer',
          offerType: rideData.currentOfferType || 'immediate',
          activeRideId: rideData.autoFollowUpActiveRideId || null,
          startedAt: rideData.currentOfferStartedAt || null,
          endedAt: Timestamp.now(),
          result: 'cancelled_dispatcher_assigned',
          assignedDriverId: driverId
        });
      }
      
      // Fahrzeugdaten nur bei sofort fahrbaren Fahrten hinzufügen.
      // Bei zukünftigen Vor-/Dauerbestellungen werden die Fahrzeugdaten bei Fahrtantritt durch die Mobile-App gesetzt.
      if (!isDeferredRideAssignment) {
        if (vehicleData) {
          baseUpdateData.driverVehicleId = driverData.car?.vehicleId || null;
          baseUpdateData.vehicleOperatorId = vehicleData.operatorId || driverData.operatorId || null;
          baseUpdateData.driverModel = vehicleData.model;
          baseUpdateData.driverLicensePlate = vehicleData.licensePlate;
        } else if (driverData.car) {
          baseUpdateData.driverVehicleId = driverData.car.vehicleId || null;
          baseUpdateData.vehicleOperatorId = driverData.operatorId || null;
          baseUpdateData.driverModel = driverData.car.model;
          baseUpdateData.driverLicensePlate = driverData.car.licensePlate;
        }
      }
      
      // Spezifische Aktualisierungsdaten je nach Bestelltyp
      let specificUpdateData = {};

      if (isDeferredRideAssignment) {
        // Vor-/Dauerbestellungen: direkt zugewiesen, acceptedAt sofort setzen
        specificUpdateData = {
          acceptedAt: Timestamp.now()
        };
      }
      
      // Kombinierte Aktualisierungsdaten
      const updateData = {
        ...baseUpdateData,
        ...specificUpdateData
      };
      
      await updateDoc(rideRef, updateData);

      if (driverData.operatorId) {
        await updateDoc(rideRef, {
          visibleToOperatorIds: arrayUnion(driverData.operatorId)
        });
      }
      
      // Nur sofort fahrbare Zuweisungen in driverLocations als aktive Fahrt markieren.
      // Zukuenftige Vor-/Dauerbestellungen bleiben bis zum Fahrtantritt ohne orderId;
      // die Mobile-App setzt orderId dann in _startRide(), sobald Live-Tracking beginnen soll.
      if (!isDeferredRideAssignment) {
        const locationRef = doc(db, 'driverLocations', driverId);
        const locationDoc = await getDoc(locationRef);
        
        if (locationDoc.exists()) {
          await updateDoc(locationRef, {
            orderId: rideId,
            timestamp: Timestamp.now()
          });
        }
      }
      
      // Push-Benachrichtigung an neuen Fahrer bei zukünftigen Vor-/Dauerbestellungen
      // (sofort fahrbare Fahrten bekommen das Annahme-Overlay über den dispatcher_pending-Listener)
      if (isDeferredRideAssignment) {
        const startAddress = rideData.start || 'Unbekannt';
        const scheduledTime = rideData.scheduledTime?.toDate?.()
          ? rideData.scheduledTime.toDate().toLocaleString('de-DE')
          : '';
        await addDoc(collection(db, 'notifications'), {
          userId: driverId,
          title: rideData.orderType === 1 ? 'Vorbestellung zugewiesen' : 'Dauerauftrag zugewiesen',
          body: `Eine Fahrt ab ${startAddress}${scheduledTime ? ` am ${scheduledTime}` : ''} wurde Ihnen zugewiesen.`,
          data: {
            type: 'ride_assigned',
            rideId: rideId,
            orderType: rideData.orderType
          },
          read: false,
          createdAt: Timestamp.now(),
          ...(driverData.fcmToken ? { fcmToken: driverData.fcmToken } : {})
        });
      }

      // Fahrt direkt laden statt über einen weiteren Thunk
      const updatedRideDoc = await getDoc(rideRef);
      return {
        id: rideId,
        ...updatedRideDoc.data()
      };
    } catch (error) {
      console.error('Error assigning ride:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateRideStatus = createAsyncThunk(
  'rides/updateStatus',
  async ({ rideId, status, additionalData = {} }, { getState, rejectWithValue, dispatch }) => {
    try {
      const rideRef = doc(db, 'rides', rideId);
      const rideDoc = await getDoc(rideRef);
      
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      const rideData = rideDoc.data();
      assertOperatorRideAccess(getCurrentUser(getState()), rideData);
      
      // Prüfen, ob Statusänderung zulässig ist
      const validStatus = ['open', 'scheduled', 'pre_accepted', 'accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver', 'dispatcher_pending'];
      if (!validStatus.includes(status)) {
        throw new Error('Ungültiger Status: ' + status);
      }
      
      // Update-Daten vorbereiten
      const updateData = {
        status,
        ...additionalData
      };
      
      // Bei Akzeptierung: is_active setzen + passenden Zeitstempel je nach Bestelltyp
      if (status === 'accepted') {
        updateData.is_active = true;
        if (rideData.orderType === 1 || rideData.orderType === 3) {
          // Vorbestellung/Dauerauftrag: startedAt markiert den Fahrtbeginn (acceptedAt wurde bei Zuweisung gesetzt)
          updateData.startedAt = Timestamp.now();
        } else {
          // Sofortbestellung: acceptedTimestamp markiert die Annahme
          updateData.acceptedTimestamp = Timestamp.now();
        }
      }

      // Status-spezifische Zeitstempel
      if (status === 'in_progress') {
        updateData.pickupTimestamp = Timestamp.now();
      } else if (status === 'completed') {
        updateData.completionTimestamp = Timestamp.now();
      } else if (status === 'cancelled' || status === 'cancelled_by_driver') {
        // Verwende unterschiedliche Zeitstempel-Felder je nach Bestelltyp
        if (rideData.orderType === 1) { // Vorbestellung
          updateData.cancelledAt = Timestamp.now();
        } else { // Sofortbestellung
          updateData.cancelledTimestamp = Timestamp.now();
        }
      }

      if (shouldAutoMarkMedicalBillingReady(rideData, updateData)) {
        updateData.medicalTransportBillingStatus = 'ready';
      }
      
      await updateDoc(rideRef, updateData);

      // Fahrer als beschäftigt markieren, wenn die Fahrt jetzt akzeptiert wird.
      // Symmetrisch zum orderId-Cleanup bei completed/cancelled weiter unten.
      // Ohne das bleibt driverLocations.orderId leer und der Dispatch wertet den
      // Fahrer trotz aktiver Fahrt als FREI (orderId=='' && isAvailable==true) —
      // genau dieser Fall trat auf, wenn der Disponent eine dispatcher_pending-
      // Anfrage im Portal manuell auf 'accepted' setzt (die Mobile-App durchläuft
      // dann ihren eigenen orderId-Schreibpfad nie).
      if (status === 'accepted' && rideData.driverId) {
        const locationRef = doc(db, 'driverLocations', rideData.driverId);
        const locationDoc = await getDoc(locationRef);

        if (locationDoc.exists()) {
          await updateDoc(locationRef, {
            orderId: rideId,
            timestamp: Timestamp.now()
          });
        }
      }

      // Fahrer-Status aktualisieren, wenn Fahrt abgeschlossen oder storniert
      if ((status === 'completed' || status === 'cancelled' || status === 'cancelled_by_driver') && rideData.driverId) {
        const driverRef = doc(db, 'users', rideData.driverId);
        const driverDoc = await getDoc(driverRef);
        
        if (driverDoc.exists()) {
          await updateDoc(driverRef, {
            isAvailable: true, // Wieder für neue Fahrten verfügbar
            lastActivityTimestamp: Timestamp.now()
          });
          
          // Fahrerstandort aktualisieren - orderId leeren
          const locationRef = doc(db, 'driverLocations', rideData.driverId);
          const locationDoc = await getDoc(locationRef);
          
          if (locationDoc.exists()) {
            await updateDoc(locationRef, {
              orderId: "",
              timestamp: Timestamp.now()
            });
          }
        }
      }
      
      // Fahrt direkt laden statt über einen weiteren Thunk
      const updatedRideDoc = await getDoc(rideRef);
      return {
        id: rideId,
        ...updatedRideDoc.data()
      };
    } catch (error) {
      console.error('Error updating ride status:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const addRideFeedback = createAsyncThunk(
  'rides/addFeedback',
  async ({ rideId, rating, comment = '' }, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!isCentralRole(getCurrentUser(getState()))) {
        throw new Error('Nur die Zentrale darf Fahrerbewertungen verwalten');
      }

      // Fahrt laden
      const rideRef = doc(db, 'rides', rideId);
      const rideDoc = await getDoc(rideRef);
      
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      const rideData = rideDoc.data();
      
      // Prüfen, ob Fahrt abgeschlossen ist
      if (rideData.status !== 'completed') {
        throw new Error('Bewertungen können nur für abgeschlossene Fahrten abgegeben werden');
      }
      
      // Fahrt aktualisieren
      await updateDoc(rideRef, {
        rating,
        comment
      });
      
      // Bewertung in der ratings-Sammlung speichern
      await addDoc(collection(db, 'ratings'), {
        rideId,
        customerId: rideData.customerId,
        driverId: rideData.driverId,
        rating,
        comment,
        timestamp: Timestamp.now()
      });
      
      // Durchschnittliche Bewertung des Fahrers aktualisieren
      if (rideData.driverId) {
        // Alle Bewertungen des Fahrers abrufen
        const ratingsQuery = query(
          collection(db, 'ratings'),
          where('driverId', '==', rideData.driverId)
        );
        
        const ratingsSnapshot = await getDocs(ratingsQuery);
        const ratings = ratingsSnapshot.docs.map(doc => doc.data().rating);
        
        if (ratings.length > 0) {
          const avgRating = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
          
          // Fahrer aktualisieren
          const driverRef = doc(db, 'users', rideData.driverId);
          await updateDoc(driverRef, {
            rating: parseFloat(avgRating.toFixed(1)),
            ratingCount: ratings.length
          });
        }
      }
      
      // Fahrt direkt laden statt über einen weiteren Thunk
      const updatedRideDoc = await getDoc(rideRef);
      return {
        id: rideId,
        ...updatedRideDoc.data()
      };
    } catch (error) {
      console.error('Error adding ride feedback:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let ridesStatusListener = null;

const initialState = {
  rides: [],
  currentRide: null,
  templates: [],
  recurringOrders: [], // Daueraufträge
  isLoading: false,
  error: null
};

// Thunk zum Aktualisieren einer Fahrt
export const fetchRideTemplates = createAsyncThunk(
  'rides/fetchRideTemplates',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Lade Fahrtenvorlagen...');
      const templatesQuery = query(
        collection(db, 'ride_templates'),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(templatesQuery);
      const templates = [];
      
      querySnapshot.docs.forEach(doc => {
        templates.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`${templates.length} Vorlagen geladen`);
      return templates;
    } catch (error) {
      console.error('Fehler beim Laden der Fahrtenvorlagen:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const saveRideTemplate = createAsyncThunk(
  'rides/saveRideTemplate',
  async (templateData, { rejectWithValue, getState }) => {
    try {
      console.log('Speichere Fahrtenvorlage:', templateData);
      
      // Pflichtfelder für eine Vorlage
      const requiredFields = ['customer_name', 'pickup_address', 'destination_address', 'vehicle_type'];
      for (const field of requiredFields) {
        if (!templateData[field]) {
          throw new Error(`Pflichtfeld ${field} fehlt in der Vorlage`);
        }
      }
      
      // Benutzer-ID des aktuellen Benutzers für Berechtigungen abrufen
      const { auth } = getState();
      const userId = auth?.user?.uid || null;
      
      // Speichern nur der relevanten Daten (keine Fahrerzuweisung)
      const templateToSave = {
        customer_name: templateData.customer_name,
        customer_phone: templateData.customer_phone || '',
        customer_id: templateData.customer_id || null,
        pickup_address: templateData.pickup_address,
        pickup_location: templateData.pickup_location || null,
        destination_address: templateData.destination_address,
        destination_location: templateData.destination_location || null,
        vehicle_type: templateData.vehicle_type,
        order_type: templateData.order_type || '2', // Default: Sofortbestellung
        created_at: Timestamp.now(),
        created_by: userId, // Für Berechtigungen in Firestore-Regeln
        template_name: templateData.template_name || `${templateData.pickup_address.split(',')[0]} → ${templateData.destination_address.split(',')[0]}`
      };
      
      // Als Dokument in die Sammlung "ride_templates" speichern
      const docRef = await addDoc(collection(db, 'ride_templates'), templateToSave);
      
      return {
        id: docRef.id,
        ...templateToSave
      };
    } catch (error) {
      console.error('Fehler beim Speichern der Fahrtenvorlage:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateRideTemplate = createAsyncThunk(
  'rides/updateRideTemplate',
  async (templateData, { rejectWithValue, getState }) => {
    try {
      console.log('Aktualisiere Fahrtenvorlage:', templateData);
      
      const { id, ...updateData } = templateData;
      
      if (!id) {
        throw new Error('Vorlagen-ID fehlt');
      }
      
      // Pflichtfelder für eine Vorlage
      const requiredFields = ['customer_name', 'pickup_address', 'destination_address', 'vehicle_type'];
      for (const field of requiredFields) {
        if (!updateData[field]) {
          throw new Error(`Pflichtfeld ${field} fehlt in der Vorlage`);
        }
      }
      
      // Benutzer-ID des aktuellen Benutzers für Berechtigungsprüfung abrufen
      const { auth } = getState();
      const userId = auth?.user?.uid || null;
      
      // Vorhandene Vorlage abrufen, um Berechtigungen zu prüfen
      const templateRef = doc(db, 'ride_templates', id);
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        throw new Error('Vorlage nicht gefunden');
      }
      
      const currentTemplate = templateDoc.data();
      
      // Prüfen, ob der Benutzer die Vorlage erstellt hat oder Admin ist
      // TODO: Für eine vollständige Implementierung sollte hier eine Admin-Prüfung erfolgen
      if (currentTemplate.created_by && currentTemplate.created_by !== userId) {
        // Diese Prüfung könnte in einer echten Anwendung umfassender sein
        // und Admin-Berechtigungen berücksichtigen
        console.warn('Berechtigungswarnung: Benutzer versucht, eine Vorlage zu bearbeiten, die er nicht erstellt hat');
      }
      
      // Zu aktualisierende Daten vorbereiten
      const templateToUpdate = {
        customer_name: updateData.customer_name,
        customer_phone: updateData.customer_phone || '',
        customer_id: updateData.customer_id || null,
        pickup_address: updateData.pickup_address,
        pickup_location: updateData.pickup_location || null,
        destination_address: updateData.destination_address,
        destination_location: updateData.destination_location || null,
        vehicle_type: updateData.vehicle_type,
        order_type: updateData.order_type || '2',
        updated_at: Timestamp.now(),
        // Originalfelder beibehalten
        created_at: currentTemplate.created_at,
        created_by: currentTemplate.created_by,
        template_name: updateData.template_name || currentTemplate.template_name || 
          `${updateData.pickup_address.split(',')[0]} → ${updateData.destination_address.split(',')[0]}`
      };
      
      // Vorlage aktualisieren
      await updateDoc(templateRef, templateToUpdate);
      
      // Aktualisierte Daten zurückgeben
      return {
        id,
        ...templateToUpdate,
        // Sicherstellen, dass die Zeitstempel für die UI korrekt konvertiert sind
        created_at: templateToUpdate.created_at.toDate ? 
          templateToUpdate.created_at.toDate().toISOString() : 
          templateToUpdate.created_at,
        updated_at: templateToUpdate.updated_at.toDate ? 
          templateToUpdate.updated_at.toDate().toISOString() : 
          templateToUpdate.updated_at
      };
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Fahrtenvorlage:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRideTemplate = createAsyncThunk(
  'rides/deleteRideTemplate',
  async (templateId, { rejectWithValue, getState }) => {
    try {
      if (!templateId) {
        throw new Error('Vorlagen-ID fehlt');
      }
      
      // Benutzer-ID für die Berechtigungsprüfung
      const { auth } = getState();
      const userId = auth?.user?.uid || null;
      
      // Vorhandene Vorlage abrufen, um Berechtigungen zu prüfen
      const templateRef = doc(db, 'ride_templates', templateId);
      const templateDoc = await getDoc(templateRef);
      
      if (!templateDoc.exists()) {
        throw new Error('Vorlage nicht gefunden');
      }
      
      const template = templateDoc.data();
      
      // Prüfen, ob der Benutzer die Vorlage erstellt hat oder Admin ist
      // TODO: Für eine vollständige Implementierung sollte hier eine Admin-Prüfung erfolgen
      if (template.created_by && template.created_by !== userId) {
        console.warn('Berechtigungswarnung: Benutzer versucht, eine Vorlage zu löschen, die er nicht erstellt hat');
      }
      
      // Vorlage löschen
      await deleteDoc(templateRef);
      
      // ID der gelöschten Vorlage zurückgeben
      return templateId;
    } catch (error) {
      console.error('Fehler beim Löschen der Fahrtenvorlage:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Dauerauftrag-Funktionen
export const fetchRecurringRides = createAsyncThunk(
  'rides/fetchRecurringRides',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Lade Daueraufträge...');
      // Suche nach Fahrten mit isRecurring = true, gruppiert nach recurringOrderId
      const recurringQuery = query(
        collection(db, 'rides'),
        where('isRecurring', '==', true),
        orderBy('scheduledTime', 'asc')
      );
      
      const querySnapshot = await getDocs(recurringQuery);
      const recurringRides = {};
      
      querySnapshot.docs.forEach(doc => {
        const ride = {
          id: doc.id,
          ...doc.data()
        };
        
        const recurringOrderId = ride.recurringOrderId;
        if (!recurringRides[recurringOrderId]) {
          recurringRides[recurringOrderId] = {
            id: recurringOrderId,
            rides: [],
            customer_name: ride.customerName,
            pickup_address: ride.start,
            destination_address: ride.destination,
            vehicle_type: ride.vehicleType,
            special_capabilities: ride.specialCapabilities || (ride.specialVehicleType ? [ride.specialVehicleType] : []),
            medicalTransportOrderId: ride.medicalTransportOrderId || null,
            medicalTransportSnapshot: ride.medicalTransportSnapshot || null,
            created_at: ride.timestamp,
            active_rides: 0,
            total_rides: 0
          };
        }
        
        recurringRides[recurringOrderId].rides.push(ride);
        recurringRides[recurringOrderId].total_rides++;
        
        // Zähle aktive Fahrten (geplant oder in Bearbeitung)
        if (['scheduled', 'pre_accepted', 'accepted', 'in_progress', 'arrived'].includes(ride.status)) {
          recurringRides[recurringOrderId].active_rides++;
        }
      });
      
      // Konvertiere zu Array und sortiere nach Erstellungsdatum
      const recurringOrders = Object.values(recurringRides).sort((a, b) => 
        new Date(b.created_at?.toDate?.() || b.created_at) - new Date(a.created_at?.toDate?.() || a.created_at)
      );
      
      console.log(`${recurringOrders.length} Daueraufträge geladen`);
      return recurringOrders;
    } catch (error) {
      console.error('Fehler beim Laden der Daueraufträge:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const pauseRecurringOrder = createAsyncThunk(
  'rides/pauseRecurringOrder',
  async (recurringOrderId, { rejectWithValue }) => {
    try {
      console.log(`Pausiere Dauerauftrag ${recurringOrderId}...`);
      
      // Suche alle zukünftigen Fahrten des Dauerauftrags
      const futureRidesQuery = query(
        collection(db, 'rides'),
        where('recurringOrderId', '==', recurringOrderId),
        where('status', '==', 'scheduled'),
        where('scheduledTime', '>', new Date())
      );
      
      const querySnapshot = await getDocs(futureRidesQuery);
      const updatePromises = [];
      
      querySnapshot.docs.forEach(doc => {
        updatePromises.push(
          updateDoc(doc.ref, {
            status: 'paused',
            pausedAt: Timestamp.now()
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      console.log(`${updatePromises.length} zukünftige Fahrten pausiert`);
      return { recurringOrderId, pausedRides: updatePromises.length };
    } catch (error) {
      console.error('Fehler beim Pausieren des Dauerauftrags:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const resumeRecurringOrder = createAsyncThunk(
  'rides/resumeRecurringOrder',
  async (recurringOrderId, { rejectWithValue }) => {
    try {
      console.log(`Reaktiviere Dauerauftrag ${recurringOrderId}...`);
      
      // Suche nur zukünftige pausierte Fahrten des Dauerauftrags.
      // Vergangene pausierte Termine bleiben dauerhaft pausiert und werden
      // nicht nachträglich als geplante Fahrten reaktiviert.
      const pausedRidesQuery = query(
        collection(db, 'rides'),
        where('recurringOrderId', '==', recurringOrderId),
        where('status', '==', 'paused'),
        where('scheduledTime', '>', Timestamp.now())
      );
      
      const querySnapshot = await getDocs(pausedRidesQuery);
      const updatePromises = [];
      
      querySnapshot.docs.forEach(doc => {
        updatePromises.push(
          updateDoc(doc.ref, {
            status: 'scheduled',
            pausedAt: null
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      console.log(`${updatePromises.length} zukünftige pausierte Fahrten reaktiviert`);
      return { recurringOrderId, resumedRides: updatePromises.length };
    } catch (error) {
      console.error('Fehler beim Reaktivieren des Dauerauftrags:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const cancelRecurringOrder = createAsyncThunk(
  'rides/cancelRecurringOrder',
  async (recurringOrderId, { rejectWithValue }) => {
    try {
      console.log(`Storniere Dauerauftrag ${recurringOrderId}...`);
      
      // Suche alle zukünftigen Fahrten des Dauerauftrags
      const futureRidesQuery = query(
        collection(db, 'rides'),
        where('recurringOrderId', '==', recurringOrderId),
        where('status', 'in', ['scheduled', 'paused']),
        where('scheduledTime', '>', new Date())
      );
      
      const querySnapshot = await getDocs(futureRidesQuery);
      const updatePromises = [];
      
      querySnapshot.docs.forEach(doc => {
        updatePromises.push(
          updateDoc(doc.ref, {
            status: 'cancelled',
            cancelledAt: Timestamp.now(),
            cancelReason: 'Dauerauftrag storniert'
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      console.log(`${updatePromises.length} zukünftige Fahrten storniert`);
      return { recurringOrderId, cancelledRides: updatePromises.length };
    } catch (error) {
      console.error('Fehler beim Stornieren des Dauerauftrags:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRecurringOrder = createAsyncThunk(
  'rides/deleteRecurringOrder',
  async (recurringOrderId, { rejectWithValue }) => {
    try {
      console.log(`Lösche Dauerauftrag ${recurringOrderId}...`);

      // Suche ALLE Fahrten des Dauerauftrags (unabhängig von Status oder Datum)
      const allRidesQuery = query(
        collection(db, 'rides'),
        where('recurringOrderId', '==', recurringOrderId)
      );

      const querySnapshot = await getDocs(allRidesQuery);
      const deletePromises = [];

      querySnapshot.docs.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });

      await Promise.all(deletePromises);

      console.log(`${deletePromises.length} Fahrten des Dauerauftrags gelöscht`);
      return { recurringOrderId, deletedRides: deletePromises.length };
    } catch (error) {
      console.error('Fehler beim Löschen des Dauerauftrags:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateRecurringOrder = createAsyncThunk(
  'rides/updateRecurringOrder',
  async ({ recurringOrderId, updateData }, { getState, rejectWithValue }) => {
    try {
      console.log(`Aktualisiere Dauerauftrag ${recurringOrderId}...`);
      const currentUser = getCurrentUser(getState());

      // Alle zukünftigen Fahrten des Dauerauftrags holen.
      // Bereits laufende/abgeschlossene Fahrten werden nicht angefasst.
      const futureRidesQuery = query(
        collection(db, 'rides'),
        where('recurringOrderId', '==', recurringOrderId),
        where('scheduledTime', '>', Timestamp.now())
      );
      const futureRidesSnapshot = await getDocs(futureRidesQuery);

      // Nur Status, die noch keinen aktiven Einsatz bedeuten, dürfen verändert werden.
      const EDITABLE_STATUSES = new Set(['scheduled', 'pre_accepted']);

      const futureRides = futureRidesSnapshot.docs.map(d => ({
        id: d.id,
        ref: d.ref,
        data: d.data(),
      }));

      const customerIdsMissingPhoneFlag = [
        ...new Set(
          futureRides
            .map(({ data }) => data)
            .filter(data => !hasOwnField(data, 'isPhoneCustomer') && data.customerId)
            .map(data => data.customerId)
        )
      ];
      const customerPhoneFlags = new Map();

      await Promise.all(customerIdsMissingPhoneFlag.map(async (customerId) => {
        try {
          const customerDoc = await getDoc(doc(db, 'users', customerId));
          if (customerDoc.exists()) {
            customerPhoneFlags.set(customerId, customerDoc.data().isPhoneCustomer === true);
          }
        } catch (error) {
          console.warn(`Kundentyp für ${customerId} konnte nicht geladen werden:`, error);
        }
      }));

      const weekdays = Array.isArray(updateData.recurring_weekdays)
        ? updateData.recurring_weekdays
        : null;
      const newTime = updateData.recurring_time ? new Date(updateData.recurring_time) : null;

      const updatePromises = [];
      const deletePromises = [];
      let lastKeptDate = null;

      futureRides.forEach(({ ref, data }) => {
        const status = data.status;
        const scheduled = data.scheduledTime?.toDate ? data.scheduledTime.toDate() : new Date(data.scheduledTime);
        const weekday = scheduled.getDay();

        const updateFields = {};

        // Stammdaten-Änderungen gelten für alle zukünftigen Fahrten (auch pre_accepted)
        if (updateData.start) updateFields.start = updateData.start;
        if (updateData.startLatitude) updateFields.startLatitude = updateData.startLatitude;
        if (updateData.startLongitude) updateFields.startLongitude = updateData.startLongitude;
        if (updateData.destination) updateFields.destination = updateData.destination;
        if (updateData.destinationLatitude) updateFields.destinationLatitude = updateData.destinationLatitude;
        if (updateData.destinationLongitude) updateFields.destinationLongitude = updateData.destinationLongitude;
        if (updateData.vehicleType) updateFields.vehicleType = updateData.vehicleType;
        if (updateData.specialCapabilities) updateFields.specialCapabilities = updateData.specialCapabilities;
        if (updateData.customerName) updateFields.customerName = updateData.customerName;
        if (updateData.customerPhone !== undefined) updateFields.customerPhone = updateData.customerPhone;
        if (updateData.isRechnungsfahrt !== undefined) {
          updateFields.isRechnungsfahrt = updateData.isRechnungsfahrt;
          updateFields.invoiceCustomerId = updateData.invoiceCustomerId ?? null;
          updateFields.invoiceCustomerName = updateData.invoiceCustomerName ?? null;
          updateFields.invoiceBillingStatus = updateData.invoiceBillingStatus ?? null;
        }

        const inferredIsPhoneCustomer = data.customerId
          ? (customerPhoneFlags.has(data.customerId) ? customerPhoneFlags.get(data.customerId) : true)
          : true;
        Object.assign(updateFields, buildMissingRecurringRideFields(data, currentUser, inferredIsPhoneCustomer));

        // Fahrt an entfallenem Wochentag → löschen (nur noch nicht aktive Fahrten)
        if (weekdays && !weekdays.includes(weekday)) {
          if (EDITABLE_STATUSES.has(status)) {
            deletePromises.push(deleteDoc(ref));
            return;
          }
          // pre_accepted mit Fahrer: nicht löschen, aber auch keine Zeitverschiebung
        }

        // Uhrzeit-Änderung auf bestehende Fahrt anwenden
        if (newTime && EDITABLE_STATUSES.has(status)) {
          const newScheduled = new Date(scheduled);
          newScheduled.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
          if (newScheduled.getTime() !== scheduled.getTime()) {
            updateFields.scheduledTime = Timestamp.fromDate(newScheduled);
          }
          if (!lastKeptDate || newScheduled > lastKeptDate) lastKeptDate = newScheduled;
        } else {
          if (!lastKeptDate || scheduled > lastKeptDate) lastKeptDate = scheduled;
        }

        if (Object.keys(updateFields).length > 0) {
          updatePromises.push(updateDoc(ref, updateFields));
        }
      });

      await Promise.all([...updatePromises, ...deletePromises]);

      // Neue Fahrten generieren:
      // a) bei Enddatum-Verlängerung, oder
      // b) wenn Wochentage hinzugefügt wurden, die im bestehenden Zeitraum noch fehlen.
      const newRidePromises = [];
      if (weekdays && newTime) {
        const lastExistingDate = lastKeptDate ? new Date(lastKeptDate) : new Date();
        const rangeStart = new Date(Math.min(Date.now(), lastExistingDate.getTime()));
        rangeStart.setHours(0, 0, 0, 0);

        const rangeEnd = updateData.newEndDate
          ? new Date(updateData.newEndDate)
          : new Date(lastExistingDate);
        rangeEnd.setHours(23, 59, 59, 999);

        // Set der bereits (nach Update) belegten Tage, damit wir keine Duplikate erzeugen.
        const occupiedDays = new Set();
        futureRides.forEach(({ data }) => {
          const scheduled = data.scheduledTime?.toDate ? data.scheduledTime.toDate() : new Date(data.scheduledTime);
          if (weekdays.includes(scheduled.getDay())) {
            const key = `${scheduled.getFullYear()}-${scheduled.getMonth()}-${scheduled.getDate()}`;
            occupiedDays.add(key);
          }
        });

        const current = new Date(rangeStart);
        while (current <= rangeEnd) {
          const currentWeekday = current.getDay();
          const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;

          if (weekdays.includes(currentWeekday) && !occupiedDays.has(key)) {
            const rideDateTime = new Date(current);
            rideDateTime.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);

            // Nur zukünftige Slots erzeugen
            if (rideDateTime.getTime() > Date.now()) {
              // Template-Fahrt zur Übernahme von Stammdaten, die nicht im
              // updateData-Patch stehen (isPhoneCustomer, Fahrttyp-Flags, Notizen,
              // Spezialfähigkeiten, Festpreis). Fällt auf die erste bekannte
              // zukünftige Fahrt zurück, damit neu generierte Slots konsistent
              // zu den bestehenden Instanzen des Dauerauftrags sind.
              const template = futureRides[0]?.data || {};
              const newRideData = {
                customerId: updateData.customerId ?? template.customerId ?? null,
                customerName: updateData.customerName ?? template.customerName ?? null,
                customerPhone: updateData.customerPhone ?? template.customerPhone ?? null,
                start: updateData.start ?? template.start,
                startLatitude: updateData.startLatitude ?? template.startLatitude,
                startLongitude: updateData.startLongitude ?? template.startLongitude,
                destination: updateData.destination ?? template.destination,
                destinationLatitude: updateData.destinationLatitude ?? template.destinationLatitude,
                destinationLongitude: updateData.destinationLongitude ?? template.destinationLongitude,
                vehicleType: updateData.vehicleType ?? template.vehicleType,
                specialCapabilities: updateData.specialCapabilities ?? template.specialCapabilities ?? [],
                notes: template.notes ?? null,
                isKrankenfahrt: template.isKrankenfahrt ?? false,
                medicalTransportOrderId: template.medicalTransportOrderId ?? null,
                medicalTransportSnapshot: template.medicalTransportSnapshot ?? null,
                medicalTransportBillingStatus: template.medicalTransportBillingStatus ?? (template.isKrankenfahrt ? 'not_ready' : null),
                medicalTransportProofStatus: template.medicalTransportProofStatus ?? (template.isKrankenfahrt ? 'missing' : null),
                isRechnungsfahrt: updateData.isRechnungsfahrt ?? template.isRechnungsfahrt ?? false,
                // invoiceCustomerId/-Name/BillingStatus dürfen bei deaktivierter
                // Rechnungsfahrt explizit null sein — daher über isRechnungsfahrt
                // gattern statt über ?? zu verketten (null ist sonst "nullish").
                invoiceCustomerId: updateData.isRechnungsfahrt !== undefined
                  ? (updateData.invoiceCustomerId ?? null)
                  : (template.invoiceCustomerId ?? null),
                invoiceCustomerName: updateData.isRechnungsfahrt !== undefined
                  ? (updateData.invoiceCustomerName ?? null)
                  : (template.invoiceCustomerName ?? null),
                invoiceBillingStatus: updateData.isRechnungsfahrt !== undefined
                  ? (updateData.invoiceBillingStatus ?? null)
                  : (template.invoiceBillingStatus ?? null),
                isPhoneCustomer: template.isPhoneCustomer ?? true,
                ...(template.isFestpreis ? {
                  isFestpreis: true,
                  festpreisEur: template.festpreisEur,
                  festpreisLabel: template.festpreisLabel,
                  estimatedEarningEur: template.estimatedEarningEur,
                } : {
                  // Nicht-Festpreis: Tarif-Schätzung des Templates übernehmen,
                  // damit neue Slots zu den Geschwister-Fahrten konsistent sind.
                  ...(template.estimatedEarningEur != null ? { estimatedEarningEur: template.estimatedEarningEur } : {}),
                }),
                // Anfahrtskosten aus dem Template übernehmen (gleiche Route wie
                // die Geschwister-Fahrten; Notiz steckt bereits in template.notes).
                ...(template.anfahrtEur != null ? { anfahrtEur: template.anfahrtEur, anfahrtKm: template.anfahrtKm ?? null } : {}),
                status: 'scheduled',
                orderType: 1,
                scheduledTime: Timestamp.fromDate(rideDateTime),
                timestamp: Timestamp.now(),
                recurringOrderId: recurringOrderId,
                isRecurring: true,
                is_active: true,
                driverId: null,
                driverName: null,
                driverProfilePic: null,
                driverModel: null,
                driverLicensePlate: null,
                driverRating: null,
                driverRatingCount: null,
                fare: null,
                estimatedTimeMinutes: null,
              };
              newRidePromises.push(addDoc(collection(db, 'rides'), newRideData));
            }
          }

          current.setDate(current.getDate() + 1);
        }

        if (newRidePromises.length > 0) {
          await Promise.all(newRidePromises);
        }
      }

      console.log(`Dauerauftrag aktualisiert: ${updatePromises.length} geändert, ${deletePromises.length} gelöscht, ${newRidePromises.length} neu.`);

      return {
        recurringOrderId,
        updatedRides: updatePromises.length,
        deletedRides: deletePromises.length,
        newRidesCount: newRidePromises.length,
      };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Dauerauftrags:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateRide = createAsyncThunk(
  'rides/updateRide',
  async (updateData, { getState, rejectWithValue }) => {
    try {
      const { rideId, ...data } = updateData;
      
      // Referenz zur Fahrt
      const rideRef = doc(db, 'rides', rideId);
      const rideDoc = await getDoc(rideRef);
      
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      const rideData = rideDoc.data();
      const currentUser = getCurrentUser(getState());
      assertOperatorRideAccess(currentUser, rideData);
      if (isOperatorRole(currentUser) && rideData.createdByOperatorId !== currentUser.operatorId) {
        throw new Error('Unternehmer duerfen nur selbst erstellte Fahrten voll bearbeiten');
      }
      
      // Prüfe, ob ein neuer Fahrer zugewiesen werden soll
      if (data.driverId && data.driverId !== rideData.driverId) {
        // Wenn ein Fahrer zugewiesen ist, aktualisiere den Status entsprechend dem Bestelltyp
        if (rideData.orderType === 1) {
          data.status = 'pre_accepted';
          data.acceptedAt = Timestamp.now();
        } else {
          data.status = 'accepted';
          data.acceptedTimestamp = Timestamp.now();
        }
        
        // Fahrerdaten laden
        const driverRef = doc(db, 'users', data.driverId);
        const driverDoc = await getDoc(driverRef);
        
        if (driverDoc.exists() && driverDoc.data().isDriver) {
          const driverData = driverDoc.data();
          
          // Fahrerdaten aktualisieren
          data.driverName = driverData.name;
          data.driverProfilePic = driverData.profilePic || '';
          data.driverRating = driverData.rating || null;
          data.driverRatingCount = driverData.ratingCount || null;
          
          // Wenn Fahrer ein Fahrzeug hat, Fahrzeugdaten aktualisieren
          if (driverData.car) {
            data.driverModel = driverData.car.model;
            data.driverLicensePlate = driverData.car.licensePlate;
          }
        }
      }
      
      // Grundlegende Datenfelder aktualisieren
      if (data.start) {
        data.startLatitude = data.startLatitude || 0;
        data.startLongitude = data.startLongitude || 0;
      }
      
      if (data.destination) {
        data.destinationLatitude = data.destinationLatitude || 0;
        data.destinationLongitude = data.destinationLongitude || 0;
      }
      
      // Timestamp-Conversion
      if (data.scheduledTime && typeof data.scheduledTime === 'string') {
        data.scheduledTime = Timestamp.fromDate(new Date(data.scheduledTime));
      }

      if (shouldAutoMarkMedicalBillingReady(rideData, data)) {
        data.medicalTransportBillingStatus = 'ready';
      }
      
      // Fahrt aktualisieren
      await updateDoc(rideRef, data);
      
      // Aktualisierte Fahrt laden
      const updatedRideDoc = await getDoc(rideRef);
      const updatedRideData = updatedRideDoc.data();
      
      // Konvertiere Zeitstempel für die Rückgabe
      const processedRide = {
        id: rideId,
        ...updatedRideData
      };
      
      const timestampFields = [
        'timestamp', 'acceptedTimestamp', 'acceptedAt', 'arrivedTimestamp', 
        'passengerPickedUpTimestamp', 'completionTimestamp', 'scheduledTime', 'startedAt'
      ];
      
      timestampFields.forEach(field => {
        if (updatedRideData[field]) {
          processedRide[field] = updatedRideData[field].toDate().toISOString();
        }
      });
      
      return processedRide;
    } catch (error) {
      console.error('Error updating ride:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Delete ride
export const deleteRide = createAsyncThunk(
  'rides/deleteRide',
  async (rideId, { getState, rejectWithValue }) => {
    try {
      const currentUser = getCurrentUser(getState());
      if (!isCentralRole(currentUser)) {
        throw new Error('Fahrten duerfen nicht geloescht werden. Bitte Fahrt stornieren.');
      }

      const rideRef = doc(db, 'rides', rideId);
      
      // Prüfen ob die Fahrt existiert
      const rideDoc = await getDoc(rideRef);
      if (!rideDoc.exists()) {
        throw new Error('Fahrt nicht gefunden');
      }
      
      // Fahrt löschen
      await deleteDoc(rideRef);
      
      return rideId;
    } catch (error) {
      console.error('Error deleting ride:', error);
      return rejectWithValue(error.message);
    }
  }
);

const ridesSlice = createSlice({
  name: 'rides',
  initialState,
  reducers: {
    clearRidesError: (state) => {
      state.error = null;
    },
    clearCurrentRide: (state) => {
      state.currentRide = null;
    },
    updateRideRealtime: (state, action) => {
      const rideData = action.payload;
      const index = state.rides.findIndex(ride => ride.id === rideData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.rides[index] = {
          ...state.rides[index],
          ...rideData
        };
      } else {
        // Wenn die Fahrt noch nicht im State ist, füge sie hinzu (neue Bestellung)
        state.rides.unshift(rideData);
      }
      
      // Auch die aktuelle Fahrt aktualisieren, falls es dieselbe ist
      if (state.currentRide && state.currentRide.id === rideData.id) {
        state.currentRide = {
          ...state.currentRide,
          ...rideData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch ride templates
      .addCase(fetchRideTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRideTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload;
      })
      .addCase(fetchRideTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Save ride template
      .addCase(saveRideTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveRideTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates.unshift(action.payload);
      })
      .addCase(saveRideTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update ride template
      .addCase(updateRideTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRideTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.templates.findIndex(template => template.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      .addCase(updateRideTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete ride template
      .addCase(deleteRideTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRideTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = state.templates.filter(template => template.id !== action.payload);
      })
      .addCase(deleteRideTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch rides
      .addCase(fetchRides.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRides.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rides = action.payload;
      })
      .addCase(fetchRides.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch ride by ID
      .addCase(fetchRideById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRideById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRide = action.payload;
      })
      .addCase(fetchRideById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create ride
      .addCase(createRide.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRide.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rides.unshift(action.payload);
        state.currentRide = action.payload;
      })
      .addCase(createRide.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Assign ride
      .addCase(assignRide.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignRide.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.rides.findIndex(ride => ride.id === action.payload.id);
        if (index !== -1) {
          state.rides[index] = action.payload;
        }
        if (state.currentRide && state.currentRide.id === action.payload.id) {
          state.currentRide = action.payload;
        }
      })
      .addCase(assignRide.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update ride status
      .addCase(updateRideStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRideStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.rides.findIndex(ride => ride.id === action.payload.id);
        if (index !== -1) {
          state.rides[index] = action.payload;
        }
        if (state.currentRide && state.currentRide.id === action.payload.id) {
          // Joins (driver/customer/vehicle) aus fetchRideById erhalten — das
          // Update-Payload ist das rohe Ride-Dokument ohne diese Objekte
          state.currentRide = {
            ...state.currentRide,
            ...action.payload,
            ...(action.payload.driverId ? {} : { driver: null, vehicle: null })
          };
        }
      })
      .addCase(updateRideStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add ride feedback
      .addCase(addRideFeedback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRideFeedback.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.rides.findIndex(ride => ride.id === action.payload.id);
        if (index !== -1) {
          state.rides[index] = action.payload;
        }
        if (state.currentRide && state.currentRide.id === action.payload.id) {
          state.currentRide = action.payload;
        }
      })
      .addCase(addRideFeedback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update ride
      .addCase(updateRide.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRide.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.rides.findIndex(ride => ride.id === action.payload.id);
        if (index !== -1) {
          state.rides[index] = action.payload;
        }
        if (state.currentRide && state.currentRide.id === action.payload.id) {
          state.currentRide = action.payload;
        }
      })
      .addCase(updateRide.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete ride
      .addCase(deleteRide.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRide.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rides = state.rides.filter(ride => ride.id !== action.payload);
      })
      .addCase(deleteRide.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Daueraufträge
      .addCase(fetchRecurringRides.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecurringRides.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recurringOrders = action.payload;
      })
      .addCase(fetchRecurringRides.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(pauseRecurringOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(pauseRecurringOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Aktualisiere den Status der entsprechenden Daueraufträge
        const { recurringOrderId } = action.payload;
        const order = state.recurringOrders.find(order => order.id === recurringOrderId);
        if (order) {
          order.status = 'paused';
        }
      })
      .addCase(pauseRecurringOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(resumeRecurringOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resumeRecurringOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Aktualisiere den Status der entsprechenden Daueraufträge
        const { recurringOrderId } = action.payload;
        const order = state.recurringOrders.find(order => order.id === recurringOrderId);
        if (order) {
          order.status = 'active';
        }
      })
      .addCase(resumeRecurringOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(cancelRecurringOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelRecurringOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Entferne den stornierten Dauerauftrag aus der Liste
        const { recurringOrderId } = action.payload;
        state.recurringOrders = state.recurringOrders.filter(order => order.id !== recurringOrderId);
      })
      .addCase(cancelRecurringOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteRecurringOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRecurringOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const { recurringOrderId } = action.payload;
        state.recurringOrders = state.recurringOrders.filter(order => order.id !== recurringOrderId);
      })
      .addCase(deleteRecurringOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateRecurringOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRecurringOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Die Dauerauftragsliste wird neu geladen nach einer Aktualisierung
      })
      .addCase(updateRecurringOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

// Thunk zum Starten des Echtzeit-Listeners für Fahrten
export const startRidesStatusListener = () => async (dispatch) => {
  console.log('Starte Echtzeit-Listener für Fahrten...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (ridesStatusListener) {
    console.log('Beende alten Fahrten-Listener...');
    ridesStatusListener();
    ridesStatusListener = null;
  }
  
  // Spezielle Anpassungen für Produktionsumgebungen
  const PROD_ENV = process.env.NODE_ENV === 'production';
  
  // Helper für zuverlässigere Echtzeit-Updates in Produktionsumgebungen
  const enhanceProductionReliability = (callback) => {
    return (...args) => {
      // In Produktionsumgebungen zusätzliche Verzögerung für stabilere Verbindungen
      if (PROD_ENV) {
        console.log('RidesSlice: Produktionsoptimierungen werden angewendet');
        setTimeout(() => callback(...args), 500);
      } else {
        callback(...args);
      }
    };
  };
  
  // Drei separate Listener einrichten:
  // 1. Ein Listener für aktive Fahrten
  // 2. Ein Listener für kürzlich stornierte Fahrten, um Statusänderungen zu erfassen
  // 3. Ein Listener für kürzlich abgeschlossene Fahrten
  const activeStatusList = ['open', 'accepted', 'in_progress', 'arrived', 'scheduled', 'pre_accepted', 'dispatcher_pending'];
  const cancelledStatusList = ['cancelled', 'cancelled_by_driver'];
  const completedStatusList = ['completed'];
  
  console.log(`Erstelle Echtzeit-Listener für aktive, stornierte und abgeschlossene Fahrten`);
  
  // Ein Zeitstempel für vor 1 Stunde, um kürzlich geänderte Fahrten zu überwachen
  const oneHourAgo = new Date();
  oneHourAgo.setHours(oneHourAgo.getHours() - 1);
  const oneHourAgoTimestamp = Timestamp.fromDate(oneHourAgo);
  
  // Hauptabfrage für aktive Fahrten
  const activeRidesQuery = query(
    collection(db, 'rides'),
    where('status', 'in', activeStatusList),
    orderBy('timestamp', 'desc'),
    firestoreLimit(30) // Begrenze auf die letzten 30 aktiven Fahrten für bessere Performance
  );
  
  // Zusätzliche Abfrage für kürzlich stornierte Fahrten
  const cancelledRidesQuery = query(
    collection(db, 'rides'),
    where('status', 'in', cancelledStatusList),
    where('timestamp', '>=', oneHourAgoTimestamp), // Fahrten aus der letzten Stunde
    orderBy('timestamp', 'desc'),
    firestoreLimit(10) // Wir brauchen nicht so viele stornierte Fahrten
  );
  
  // Zusätzliche Abfrage für kürzlich abgeschlossene Fahrten
  const completedRidesQuery = query(
    collection(db, 'rides'),
    where('status', 'in', completedStatusList),
    where('timestamp', '>=', oneHourAgoTimestamp), // Fahrten aus der letzten Stunde
    orderBy('timestamp', 'desc'),
    firestoreLimit(10) // Wir brauchen nicht so viele abgeschlossene Fahrten
  );
  
  // Funktion zur Verarbeitung der Änderungen in den Snapshots
  const processRideChanges = async (snapshot) => {
    for (const change of snapshot.docChanges()) {
      const rideData = change.doc.data();
      const rideId = change.doc.id;

      // Nur bei neuen oder geänderten Dokumenten
      if (change.type === 'added' || change.type === 'modified') {
        console.log(`Echtzeit-Update für Fahrt ${rideId}, Status: ${rideData.status}`);

        // Sound-Benachrichtigungen werden zentral im AppDataManager verwaltet
        
        // Verarbeite die Fahrtdaten ähnlich wie in fetchRides
        const processedRide = {
          id: rideId,
          ...rideData
        };
        
        // Konvertiere alle Zeitstempel für einfachere Handhabung
        const timestampFields = [
          'timestamp', 'acceptedTimestamp', 'acceptedAt', 'arrivedTimestamp', 
          'passengerPickedUpTimestamp', 'completionTimestamp', 'scheduledTime', 'startedAt'
        ];
        
        timestampFields.forEach(field => {
          if (rideData[field]) {
            processedRide[field] = rideData[field].toDate().toISOString();
          }
        });
        
        // Fahrerdaten laden, falls vorhanden
        if (rideData.driverId) {
          try {
            const driverDoc = await getDoc(doc(db, 'users', rideData.driverId));
            if (driverDoc.exists() && driverDoc.data().isDriver) {
              const driverData = driverDoc.data();
              processedRide.driver = {
                id: rideData.driverId,
                ...driverData,
                user: {
                  id: rideData.driverId,
                  name: driverData.name,
                  email: driverData.email,
                  phone: driverData.phone || '',
                  profile_picture: driverData.profilePic || ''
                }
              };
            }
          } catch (error) {
            console.error(`Fehler beim Laden der Fahrerdaten für Fahrt ${rideId}:`, error);
          }
        }
        
        // Aktualisierung an den Store senden
        dispatch({
          type: 'rides/updateRideRealtime',
          payload: processedRide
        });
      }
    }
  };
  
  // Ein Array für alle Listener-Unsubscribe-Funktionen
  const listeners = [];
  
  // In Produktionsumgebungen robusteres Error-Handling
  const errorHandler = (listenerName) => (error) => {
    console.error(`Fehler im ${listenerName}:`, error);
    
    if (PROD_ENV) {
      console.log(`RidesSlice: Versuche ${listenerName} neu zu starten...`);
      // Im Produktionsmodus nach Fehlern neu starten
      setTimeout(() => {
        try {
          dispatch(startRidesStatusListener());
          console.log('RidesSlice: Listener erfolgreich neu gestartet');
        } catch (e) {
          console.error('RidesSlice: Neustart des Listeners fehlgeschlagen:', e);
        }
      }, 2000);
    }
  };
  
  // Wrapper für zuverlässigere Callbacks in Produktionsumgebungen
  const processRideChangesWithEnhancement = enhanceProductionReliability(async (snapshot) => {
    await processRideChanges(snapshot);
    
    if (PROD_ENV) {
      console.log('RidesSlice: Fahrten-Updates verarbeitet', 
        {timestamp: new Date().toISOString(), changes: snapshot.docChanges().length});
    }
  });
  
  // Listener für aktive Fahrten mit erweiterten Optionen für Produktion
  const activeRidesListener = onSnapshot(
    activeRidesQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processRideChangesWithEnhancement,
    errorHandler('aktiven Fahrten-Listener')
  );
  listeners.push(activeRidesListener);
  
  // Listener für stornierte Fahrten
  const cancelledRidesListener = onSnapshot(
    cancelledRidesQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processRideChangesWithEnhancement,
    errorHandler('stornierten Fahrten-Listener')
  );
  listeners.push(cancelledRidesListener);
  
  // Listener für abgeschlossene Fahrten
  const completedRidesListener = onSnapshot(
    completedRidesQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processRideChangesWithEnhancement,
    errorHandler('abgeschlossenen Fahrten-Listener')
  );
  listeners.push(completedRidesListener);
  
  // Kombinierte Unsubscribe-Funktion für alle drei Listener
  ridesStatusListener = () => {
    listeners.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (e) {
          console.error('Fehler beim Aufräumen eines Rides-Listeners:', e);
        }
      }
    });
  };
  
  if (PROD_ENV) {
    console.log('RidesSlice: Echtzeit-Listener erfolgreich gestartet (Produktionsmodus)');
  }
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (ridesStatusListener) {
      ridesStatusListener(); // Diese Funktion ruft jetzt alle Listener auf
      ridesStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopRidesStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Fahrten...');
  if (ridesStatusListener) {
    ridesStatusListener();
    ridesStatusListener = null;
  }
};

export const { clearRidesError, clearCurrentRide, updateRideRealtime } = ridesSlice.actions;
export default ridesSlice.reducer;
